/**
 * Tables stay with their cameras. Matches (rubbers) move onto those tables.
 */

export function normalizeChampionshipTable(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1 || n > 999) return null;
  return Math.round(n);
}

export function championshipTablesFromSnapshot(snapshot, extras = []) {
  const set = new Set([1, 2]);
  for (const cam of snapshot?.cameras || []) {
    const n = normalizeChampionshipTable(cam?.table_number);
    if (n) set.add(n);
  }
  for (const row of snapshot?.rubbers || []) {
    const n = normalizeChampionshipTable(row?.table_number);
    if (n) set.add(n);
  }
  for (const raw of extras || []) {
    const n = normalizeChampionshipTable(raw);
    if (n) set.add(n);
  }
  return [...set].sort((a, b) => a - b);
}

export function liveRubberOnTable(rubbers, tableNumber, exceptRubberId = null) {
  const table = normalizeChampionshipTable(tableNumber);
  if (!table) return null;
  return (
    (rubbers || []).find(
      (row) =>
        normalizeChampionshipTable(row?.table_number) === table &&
        row?.state === "live" &&
        String(row?.rubber_id) !== String(exceptRubberId || "")
    ) || null
  );
}

export function canAssignRubberTable(rubber, tableNumber, rubbers = []) {
  if (!rubber || rubber.state === "done") return false;
  const table = normalizeChampionshipTable(tableNumber);
  if (!table) return false;
  if (normalizeChampionshipTable(rubber.table_number) === table) return true;
  return !liveRubberOnTable(rubbers, table, rubber.rubber_id);
}

export function assignableRubbersForTable(rubbers, tableNumber) {
  return (rubbers || []).filter((row) =>
    canAssignRubberTable(row, tableNumber, rubbers)
  );
}

export function camerasOnTable(cameras, tableNumber) {
  const table = normalizeChampionshipTable(tableNumber);
  if (!table) return [];
  return (cameras || []).filter(
    (cam) => normalizeChampionshipTable(cam?.table_number) === table
  );
}

export function tableHasOnlineCamera(cameras, tableNumber) {
  return camerasOnTable(cameras, tableNumber).some(
    (cam) => Boolean(cam?.online || cam?.socket_id)
  );
}

function firstNames(list) {
  return (list || [])
    .map((name) => String(name || "").trim())
    .filter(Boolean)
    .map((name) => name.split(/\s+/)[0])
    .join("/");
}

export function rubberMatchupLabel(row, snapshot) {
  const home =
    firstNames(row?.home_names) ||
    snapshot?.fixture?.home?.display_name ||
    snapshot?.match?.home_display_name ||
    "Home";
  const away =
    firstNames(row?.away_names) ||
    snapshot?.fixture?.away?.display_name ||
    snapshot?.match?.away_display_name ||
    "Away";
  return `${home} vs ${away}`;
}

export function rubberCodeLabel(code) {
  if (!code) return "";
  return String(code).startsWith("D") ? code : String(code).replace("x", "–");
}

/** Prefer a table that already has a live camera when the current one does not. */
export function suggestedCameraTable(rubber, snapshot, tables = null) {
  if (!rubber || rubber.state === "done") return null;
  const cameras = snapshot?.cameras || [];
  const current = normalizeChampionshipTable(rubber.table_number);
  if (current && tableHasOnlineCamera(cameras, current)) return null;
  const options = tables || championshipTablesFromSnapshot(snapshot);
  return (
    options.find(
      (n) =>
        n !== current &&
        tableHasOnlineCamera(cameras, n) &&
        canAssignRubberTable(rubber, n, snapshot?.rubbers || [])
    ) || null
  );
}
